import { createGlobalStyle } from 'styled-components';
import { rem, lighten } from '@helpers/style';

import fontIconEOT from 'assets/fonts/nahartime-icon/NaharTime.eot';
import fontIconTTF from 'assets/fonts/nahartime-icon/NaharTime.ttf';
import fontIconWOFF from 'assets/fonts/nahartime-icon/NaharTime.woff';
import fontIconSVG from 'assets/fonts/nahartime-icon/NaharTime.svg';
import {breakpoint} from "styles/mixins";

const GlobalStyle = createGlobalStyle`
  * {
		box-sizing: border-box;
	}

  @font-face {
    font-family: 'NaharTime';
    src: url('${fontIconEOT}?xclb7t');
    src: url('${fontIconEOT}?xclb7t#iefix')
        format('embedded-opentype'),
      url('${fontIconTTF}?xclb7t')
        format('truetype'),
      url('${fontIconWOFF}?xclb7t')
        format('woff'),
      url('${fontIconSVG}?xclb7t#NaharTime')
        format('svg');
    font-weight: normal;
    font-style: normal;
  }

	html,
	body {
		-webkit-tap-highlight-color: transparent;
	}

	body {
		font-family: ${(p) => p.theme.fontFamily.primary};
		color: ${(p) => p.theme.color.textColor};
		font-size: 1rem;
		background-color: ${(p) => p.theme.color.white};
		min-width: 320px;
		overflow-x: hidden;
		opacity: 1;
		margin: 0;
		padding: 0;
    width: 100%;
	}

	button,
	input,
	textarea,
	select,
	option {
		font-family: ${(p) => p.theme.fontFamily.primary};
		font-size: 1rem;
		-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
		-webkit-tap-highlight-color: transparent;
		outline: 0;
	}

	figure {
		margin: 0;
	}

	img {
		border: 0;
	}

	a {
		text-decoration: none;
		color: ${(p) => p.theme.color.linkColor};
	}

	ul {
		padding: 0;
		margin: 0;
		list-style: none;
	}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 600;
    line-height: 1.5;
    margin: 0 0 0.5em;
    word-spacing: -0.09375em;
  }

  h1 {
    margin: 0;
    font-size: 25.22845px;
  }
  h2 {
    font-size: 22.42529px;
  }
  h3 {
    font-size: 19.93359px;
  }
  h4 {
    font-size: 17.71875px;
  }
  h5 {
    font-size: 15.75px;
  }
  h6 {
    font-size: 14px;
  }
  hr {
    margin: 0.5rem 0;
    padding: 0;
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

	table {
		width: 100%;
		border-collapse: collapse;
		direction: rtl;
		font-size: ${(p) => rem(14, p.theme.fontSize.base)};
  	border-spacing: 0;
		thead {
      font-size:12px;
      color: #a7a7a7;
			tr {
				cursor: pointer;
			}

			th {
				border-bottom: 2px solid ${(p) => p.theme.color.border};
        font-weight: 400;
				font-size: ${(p) => rem(13, p.theme.fontSize.base)};
        padding: 10px;
        white-space: nowrap;
				text-align: center;
			}
		}

		tbody {
			tr {
				&:last-child {
					border-bottom: 0 none;
				}

				&:nth-child(odd) {
					background-color: #fff;
				}

				td {
          padding: 10px;
					text-align: center;
          font-size:12px; 
          white-space: nowrap;
        }
				}
			}
		}
	}

	input:-webkit-autofill,
	input:-webkit-autofill:hover,
	input:-webkit-autofill:focus,
	textarea:-webkit-autofill,
	textarea:-webkit-autofill:hover,
	textarea:-webkit-autofill:focus,
	select:-webkit-autofill,
	select:-webkit-autofill:hover,
	select:-webkit-autofill:focus {
		transition: background-color 5000s ease-in-out 0s;
	}

	.loader {
		width: 20px;
		height: 20px;
		position: relative;
		animation: loader 0.8s infinite linear;
		border: 3px solid hsla(0, 0%, 100%, 0.2);
		border-left-color: #fff;
		display: inline-block;
		vertical-align: middle;
		border-radius: 50%;
	}
	@keyframes loader {
		0% {
			transform: rotate(0deg);
		}
		50% {
			transform: rotate(1turn);
		}
		to {
			transform: rotate(2turn);
		}
	}

  .clearfix {
    &:after {
      display: block;
      clear: both;
      content: '';
    }
  }

  .fix-height {
    float: right;
    width: 100%;
  }

  .ltr{
    direction: ltr;
  }

	.icon {
		/* use !important to prevent issues with browser extensions that change fonts */
		font-family: 'NaharTime' !important;
		font-style: normal;
		font-weight: normal;
		font-variant: normal;
		text-transform: none;
		line-height: 1;
		vertical-align: middle;
		padding-top: 1px;

		/* Better Font Rendering =========== */
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	.thin-scrollbar {
		&::-webkit-scrollbar {
			width: 5px;
			background-color: ${(p) => lighten(p.theme.color.graySuperlight, 5)};
		}

		&::-webkit-scrollbar-thumb {
			background-color: ${(p) => lighten(p.theme.color.grayDark, 50)};
		}
	}

	.en-num {
		font-family: Arial;
	}

	.placeholder-anim {
		animation-duration: 1.7s;
		animation-fill-mode: forwards;
		animation-iteration-count: infinite;
		animation-timing-function: linear;
		animation-name: placeholderAnimate;
		background: ${(p) => p.theme.color.placeholderLight};
		background: linear-gradient(
			to right,
			${(p) => p.theme.color.placeholder} 10%,
			${(p) => p.theme.color.placeholderShine} 18%,
			${(p) => p.theme.color.placeholder} 34%
		);
		background-size: 1300px;
	}

	@keyframes placeholderAnimate {
		0% {
			background-position: 650px 0;
		}
		100% {
			background-position: -650px 0;
		}
	}

  .ml-1 {
    margin-left: 5px;
  }
  .ml-2 {
    margin-left: 10px;
  }
  .ml-3 {
    margin-left: 15px;
  }
  .mr-1 {
    margin-right: 5px;
  }
  .mr-2 {
    margin-right: 10px;
  }
  .mr-3 {
    margin-right: 15px;
  }
  .mb-1 {
    margin-bottom: 5px;
  }
  .mb-2 {
    margin-bottom: 10px;
  }
  .mb-3 {
    margin-bottom: 15px;
  }
  .mt-1 {
    margin-top: 5px;
  }
  .mt-2 {
    margin-top: 10px;
  }
  .mt-3 {
    margin-top: 15px;
  }
  .banner-container{
    display:flex;
    justify-content:center;
    margin-bottom:30px;
    border-radius: 10px;
  }
  .desktop-banner{
    width:640px;
    height:120px;
    border-radius: 10px;
  }
  .mobile-banner{
    width:370px;
    height:120px;
    border-radius: 10px;
  }
.woot-widget-bubble{
    
${breakpoint.lessThan('desktop')`
  	bottom:70px !important;  
  `}
}
  `;

export default GlobalStyle;
